import { CommonModule } from "@angular/common";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { MessageService } from "primeng/api";
import { Subject, takeUntil } from "rxjs";
import { FileUploadModalComponent } from "../../../components/modal-templates/file-upload-modal/file-upload-modal.component";
import { SharedComponents } from "../../../components/shared.module";
import {
  resetDashboardConfig,
  TestReportSummarizationActions,
} from "../../../core/ngrx/actions/report-summarization.action";
import { selectRelease } from "../../../core/ngrx/selectors/release.selectors";
import {
  selectTestReportSummarizationData,
  selectTestReportSummarizationLoading,
  selectTestReportSummarizationMetaData,
} from "../../../core/ngrx/selectors/report-summarization.selectors";
import { MaterialModule } from "../../../core/shared/material.module";
import { MessageEnum } from "../../../core/utils/message.enum";
import { BaseLayoutComponent } from "../../base-layout.component";

/**
 * Component for summarizing and displaying test report data.
 */
@Component({
  selector: "app-test-report-summarization",
  standalone: true,
  imports: [
    CommonModule,
    ...SharedComponents,
    FormsModule,
    MatSlideToggleModule,
    MatIconModule,
    MaterialModule,
    NzTabsModule,
    NzSkeletonModule,
  ],
  templateUrl: "./test-report-summarization.component.html",
  styleUrl: "./test-report-summarization.component.scss",
})
export class TestReportSummarizationComponent
  extends BaseLayoutComponent
  implements OnInit, OnDestroy
{
  private readonly destroy$ = new Subject<void>();
  private readonly RELEASE_BREADCRUMB_KEY = 'trs-release-filter';
  toggleSprint: any;
  selectedSprint: any;
  dashboardConfig$ = this.store.select(selectTestReportSummarizationData);
  metaData$ = this.store.select(selectTestReportSummarizationMetaData);
  loading$ = this.store.select(selectTestReportSummarizationLoading);
  isLoading: boolean = true;
  data: any = [];
  selectedFile: any;
  isUploading: boolean = false;
  isSyncing: boolean = false;
  private modalService = inject(NgbModal);
  releases$: any = [];
  formData: any;
  private messageService = inject(MessageService);
  dashboard: any = [];
  releases: any = [];
  releasesDropdownData: any = [];
  formattedDateRange: string = '';
  activeGroupIndex = 0;
  /**
   * Lifecycle hook executed when the component is initialized.
   * Subscribes to the loading state and dispatches an action to load test report summarization data.
   */
  ngOnInit(): void {
    this.loadData();
  }

  loadData() {
    this.loading$.subscribe((loading) => {
      this.isLoading = loading;
    });

    const dataStore = this.dataStore?.data;
    this.store.dispatch(
      TestReportSummarizationActions.loadTestReportSummarization({
        view: "TRS",
        projectId: dataStore?.id,
        requestParam: {
          isAggregated: true,
        },
      })
    );

    this.releases$ = this.store.select(selectRelease);
    this.releases$.subscribe((data: any[]) => {
      this.releasesDropdownData = [];
      this.releases =
        data?.map((release) => ({
          value: release.trsReleaseId,
          label: release.name,
        })) || [];

      this.releasesDropdownData.push({
        value: "All_Release",
        label: "All Releases",
      });

      this.releasesDropdownData.push(...this.releases);
      this.selectedSprint = this.releasesDropdownData[0].value;

      // Register / update the releases dropdown as a breadcrumb node
      const breadcrumbNode = {
        name: "All Releases",
        key: this.RELEASE_BREADCRUMB_KEY,
        isDropdown: true,
        selectedDropdownValue: "All_Release",
        dropdownItems: this.releasesDropdownData.map((r: any) => ({
          label: r.label,
          value: r.value,
        })),
      };
      const exists = this.breadcrumbService.list.some(
        (item: any) => item.key === this.RELEASE_BREADCRUMB_KEY
      );
      if (exists) {
        this.breadcrumbService.updateNode(breadcrumbNode);
      } else {
        this.breadcrumbService.addNode(breadcrumbNode);
      }
    });

    // Listen for release selection changes from the breadcrumb dropdown
    this.breadcrumbService.onDropdownChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ key, value }) => {
        if (key === this.RELEASE_BREADCRUMB_KEY) {
          this.selectedSprint = value;
          this.onSelectionChange();
        }
      });

    this.metaData$.subscribe(item => {
      console.log(item);
      if (item?.startDate && item?.endDate) {
        const start = this.datePipe.transform(item.startDate, 'MMMM d, y');
        const end = this.datePipe.transform(item.endDate, 'MMMM d, y');
        this.formattedDateRange = `${start} - ${end}`;
        this.updatePageActions();
      }
    });

    // Register upload/sync icons in the breadcrumb header bar
    this.updatePageActions();

    // Handle icon clicks from the breadcrumb bar
    this.breadcrumbService.onPageAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe(key => {
        if (key === 'upload') this.openFileUpload();
        else if (key === 'syncJira') this.syncJira();
      });
  }

  // Method to get Bootstrap class based on the size attribute in JSON config
  getTileClass(size: string): string {
    return size || "col-md-12"; // Default to full width if no size is provided
  }

  getGroupTitle(group: any): string {
    return group?.groupDisplayName ?? '';
  }

  openFileUpload() {
    const fileUploadConfig = {
      fileUploadLabel: "Upload File",
      fileTypes: ".csv, .xlsx",
      buttonLabel: "Choose file",
      placeholderText: "CSV, Excel files only",
    };
    const modalRef = this.modalService.open(FileUploadModalComponent, {
      centered: true,
      size: "lg",
      windowClass: "add-item-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = {
      title: "Upload File",
      file: fileUploadConfig,
      projectId: this.dataStore?.data?.id,
    };
    modalRef.componentInstance.releases = this.releases;

    modalRef.result
      .then((result) => {
        this.isUploading = true;
        this.updatePageActions();
        console.log("selectedRelease",result.value);

        this.formData = new FormData();
        this.formData.append("file", result?.value?.file);
        this.formData.append("sprintId", result?.value?.release);

        this.restService
          .uploadReportFile(this.dataStore?.data?.id, this.formData)
          .subscribe({
            next: (response) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.FILE_UPLOAD_SUCCESS });
              this.isUploading = false;
              this.updatePageActions();
              //resync data
              this.loadData();
            },
            error: (error) => {
              const errorMsg = error?.message
                ? error?.message
                : MessageEnum.FILE_UPLOAD_FAILURE;
              this.toaster.pop({ type: 'error', text: errorMsg });
              this.isUploading = false;
              this.updatePageActions();
              console.error(error);
            },
          });
      })
      .catch((error) => {});
  }

  onSelectionChange() {
    console.log("selected releaset", this.selectedSprint);
    //get the data metric for selected release/sprint
    if (this.selectedSprint === "All_Release") {
      this.loadData();
    } else {
      this.store.dispatch(resetDashboardConfig());
      this.store.dispatch(
        TestReportSummarizationActions.loadTestReportSummarization({
          view: "TRS",
          projectId: this.dataStore?.data?.id,
          requestParam: {
            isAggregated: false,
            sprint: this.selectedSprint,
          },
        })
      );
    }

    this.isLoading = true;
  }

  private updatePageActions(): void {
    const actions: any[] = [];
    if (this.formattedDateRange) {
      actions.push({
        key: 'date',
        label: this.formattedDateRange,
        icon: 'assets/images/virtusa-icons/calender.svg',
        isTextOnly: true,
      });
    }
    actions.push(
      {
        key: 'upload',
        label: 'Upload Manual Execution Results',
        icon: 'assets/images/virtusa-icons/upload-trs-new.svg',
        iconOnly: true,
        isLoading: this.isUploading,
      },
      {
        key: 'syncJira',
        label: 'Sync With ALM To Extract Defects',
        icon: 'assets/images/virtusa-icons/sync-trs-new.svg',
        iconOnly: true,
        isLoading: this.isSyncing,
      },
    );
    this.breadcrumbService.setPageActions(actions);
  }

  syncJira() {
    this.isSyncing = true;
    this.updatePageActions();
    this.restService.syncJira(this.dataStore?.data?.id).subscribe({
      next: (response) => {
        this.isSyncing = false;
        this.updatePageActions();
        //reload data with the currently selected release
        if (this.selectedSprint === "All_Release") {
          this.loadData();
        } else {
          this.store.dispatch(resetDashboardConfig());
          this.store.dispatch(
            TestReportSummarizationActions.loadTestReportSummarization({
              view: "TRS",
              projectId: this.dataStore?.data?.id,
              requestParam: {
                isAggregated: false,
                sprint: this.selectedSprint,
              },
            })
          );
        }
      },
      error: (error) => {
        this.isSyncing = false;
        this.updatePageActions();
        console.error(error);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.breadcrumbService.clearPageActions();
  }
}
